import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useGetOrganisationQuery } from '@/app/services/organisation'
import {
    selectHasAdvancedMaterials,
    selectHasUnfoldTube,
    selectIsOnTrial,
    selectOrganisationId,
} from '@/app/slices/organisationSlice'

import { useToolBoxTreatments } from './useToolBoxTreatments'

/**
 * Custom hook that determines whether the Cutting Technology Selection Modal should be displayed.
 *
 * This hook evaluates multiple conditions to determine if an organization needs to be shown
 * the cutting technology selection modal:
 *
 * 1. **Technology Count**: Organization has MORE than 1 unarchived cutting technology (flat or rotary)
 * 2. **Trial Status**: Organization is NOT on trial plan
 * 3. **Advanced Features**: Organization does NOT have advanced materials OR unfold tube features enabled
 * 4. **Feature Flag**: Split.io ToolBox_CuttingTechSelectionModal is On/True
 * 5. **Route Exclusion**: Current route is NOT /settings/account#subscription or /processes
 *
 * @returns {Object} Object containing:
 *   - shouldShowModal {boolean} - Whether the modal should be displayed
 *   - cuttingTechnologies {Array} - Filtered unarchived cutting technologies
 *   - isLoading {boolean} - Loading state from the API query
 *   - error {any} - Error state from the API query
 *
 * @example
 * ```jsx
 * const { shouldShowModal, cuttingTechnologies, isLoading, error } = useCuttingTechModal()
 *
 * return (
 *   <CuttingTechSelectionModal
 *     open={shouldShowModal}
 *     onClose={handleClose}
 *   />
 * )
 * ```
 */
export const useCuttingTechModal = () => {
    const location = useLocation()
    const organisationId = useSelector(selectOrganisationId)
    const isOnTrial = useSelector(selectIsOnTrial)
    const hasAdvancedMaterials = useSelector(selectHasAdvancedMaterials)
    const hasUnfoldTube = useSelector(selectHasUnfoldTube)
    const { showCuttingTechModal } = useToolBoxTreatments()

    // Get organisation fetching state to prevent modal flash during refetch
    const { isFetching: isOrganisationFetching } = useGetOrganisationQuery(
        { organisationId },
        { skip: !organisationId }
    )

    // Get cutting technologies from API
    const {
        data: cuttingTechnologiesData,
        error,
        isLoading,
    } = useGetCuttingTechnologiesQuery({ organisationId }, { skip: !organisationId })

    // Filter unarchived cutting technologies
    const cuttingTechnologies = useMemo(() => {
        if (!cuttingTechnologiesData) return []

        return Object.values(cuttingTechnologiesData).filter((tech) => !tech.isDeleted)
    }, [cuttingTechnologiesData])

    // Determine if modal should be shown
    const shouldShowModal = useMemo(() => {
        // Skip if loading, fetching organisation, or has error
        // This prevents modal flash during organisation refetch (e.g., after settings save)
        if (isLoading || error || isOrganisationFetching) {
            return false
        }

        // Condition 1: Organization has MORE than 1 unarchived cutting technology
        const hasMutipleTechnologies = cuttingTechnologies.length > 1

        // Condition 2: Organization is NOT on trial plan
        const notOnTrial = !isOnTrial

        // Condition 3: Organization does NOT have advanced materials OR unfold tube features
        const hasAdvancedFeatures = hasAdvancedMaterials || hasUnfoldTube
        const lacksAdvancedFeatures = !hasAdvancedFeatures

        // Condition 4: Split.io ToolBox_CuttingTechSelectionModal is On/True
        const featureFlagEnabled = showCuttingTechModal

        // Condition 5: Current route is NOT /settings/account or /processes-configuration
        const excludedRoutes = ['/settings/account', '/process-configuration']
        const notOnExcludedRoute = !excludedRoutes.includes(location.pathname)

        // All conditions must be true
        return hasMutipleTechnologies && notOnTrial && lacksAdvancedFeatures && featureFlagEnabled && notOnExcludedRoute
    }, [
        isLoading,
        error,
        isOrganisationFetching,
        cuttingTechnologies.length,
        isOnTrial,
        hasAdvancedMaterials,
        hasUnfoldTube,
        showCuttingTechModal,
        location.pathname,
    ])

    return {
        shouldShowModal,
        cuttingTechnologies,
        isLoading,
        error,
    }
}
